// src/pages/hr/learning/LearningReportsTab.jsx
// ============================================================
// Learning & Development - Reports
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useState } from "react";
import {
  Download,
  Printer,
  FileText,
  Users,
  Award,
  BookOpen,
  Building,
} from "lucide-react";

export default function LearningReportsTab({
  learningState,
  employees = [],
  departments = [],
}) {
  const { data } = learningState;
  const courses = data?.courses || [];
  const progressRecords = data?.progressRecords || [];

  const [selectedReport, setSelectedReport] = useState("course-completion");

  const reports = [
    { id: "course-completion", label: "Course Completion Report", icon: <BookOpen size={16} strokeWidth={1.8} /> },
    { id: "quiz-results", label: "Quiz Results Report", icon: <Award size={16} strokeWidth={1.8} /> },
    { id: "department-progress", label: "Department Progress Report", icon: <Building size={16} strokeWidth={1.8} /> },
    { id: "employee-training", label: "Employee Training Summary", icon: <Users size={16} strokeWidth={1.8} /> },
    { id: "audit-log", label: "L&D Audit Log", icon: <FileText size={16} strokeWidth={1.8} /> },
  ];

  // ── Export Helpers ──────────────────────────────────────────────────────
  const exportCSV = (headers, rows, filename) => {
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  // ── Report Data Generators ──────────────────────────────────────────────
  const renderCourseCompletionReport = () => {
    const rows = courses.map((c) => {
      const completed = progressRecords.filter((p) => p.courseId === c.id && p.videoCompleted).length;
      const quizzed = progressRecords.filter((p) => p.courseId === c.id && p.quizAttempted).length;
      const passed = progressRecords.filter((p) => p.courseId === c.id && p.passed).length;
      const assigned = c.assignedToNames?.length || employees.length;
      return { ...c, assigned, completed, quizzed, passed };
    });

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginBottom: "1rem" }}>
          <button className="btn-outline-action" onClick={() => exportCSV(
            ["Course", "Category", "Department", "Assigned", "Video Completed", "Quiz Attempted", "Passed"],
            rows.map((r) => [r.title, r.category, r.department, r.assigned, r.completed, r.quizzed, r.passed]),
            "course_completion_report"
          )} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Download size={14} strokeWidth={1.8} /> Export CSV
          </button>
          <button className="btn-outline-action" onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Printer size={14} strokeWidth={1.8} /> Print PDF
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", fontSize: "0.83rem", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Course Title</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Category</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Dept</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Assigned</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Video Done</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Quiz Done</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Passed</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Completion %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "0.7rem 0.85rem", fontWeight: 700, color: "#0f172a" }}>{r.title}</td>
                  <td style={{ padding: "0.7rem 0.85rem" }}><span className="lnd-green-badge">{r.category}</span></td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#475569" }}>{r.department}</td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#475569" }}>{r.assigned}</td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#15803d", fontWeight: 700 }}>{r.completed}</td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#475569" }}>{r.quizzed}</td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#15803d", fontWeight: 700 }}>{r.passed}</td>
                  <td style={{ padding: "0.7rem 0.85rem" }}>
                    <span className="lnd-green-badge">
                      {r.assigned > 0 ? Math.round((r.completed / r.assigned) * 100) : 0}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuizResultsReport = () => {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginBottom: "1rem" }}>
          <button className="btn-outline-action" onClick={() => exportCSV(
            ["Employee", "Course", "Score", "Passed", "Attempt Date"],
            progressRecords.filter(p => p.quizAttempted).map((p) => [
              p.employeeName || p.userName || "Employee",
              p.courseTitle || p.courseName || "Course",
              `${p.score ?? p.quizScore}%`,
              p.passed ? "Yes" : "No",
              p.quizAttemptedAt?.toDate ? p.quizAttemptedAt.toDate().toLocaleDateString() : "Recent"
            ]),
            "quiz_results_report"
          )} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Download size={14} strokeWidth={1.8} /> Export CSV
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", fontSize: "0.83rem", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Employee</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Course Title</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Score</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Result</th>
                <th style={{ textAlign: "left", padding: "0.65rem 0.85rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {progressRecords.filter(p => p.quizAttempted).map((p, idx) => (
                <tr key={p.id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "0.7rem 0.85rem", fontWeight: 700, color: "#0f172a" }}>{p.employeeName || p.userName || "Employee"}</td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#334155" }}>{p.courseTitle || p.courseName || "Course"}</td>
                  <td style={{ padding: "0.7rem 0.85rem", fontWeight: 700, color: "#15803d" }}>{p.score ?? p.quizScore}%</td>
                  <td style={{ padding: "0.7rem 0.85rem" }}>
                    <span className="lnd-green-badge">
                      {p.passed ? "Passed" : "Failed"}
                    </span>
                  </td>
                  <td style={{ padding: "0.7rem 0.85rem", color: "#64748b" }}>
                    {p.quizAttemptedAt?.toDate ? p.quizAttemptedAt.toDate().toLocaleDateString() : "Recent"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .lnd-green-badge {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          padding: 0.45rem 0.85rem;
          font-weight: 700;
          font-size: 0.78rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
      `}</style>

      {/* Reports Nav Pills */}
      <div className="card" style={{ padding: "0.9rem 1.15rem", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {reports.map((rep) => (
            <button
              key={rep.id}
              onClick={() => setSelectedReport(rep.id)}
              style={{
                padding: "0.5rem 0.9rem",
                fontSize: "0.82rem",
                fontWeight: 700,
                borderRadius: "8px",
                border: selectedReport === rep.id ? "1.5px solid #15803d" : "1.5px solid #e2e8f0",
                cursor: "pointer",
                background: selectedReport === rep.id ? "#f0fdf4" : "#ffffff",
                color: selectedReport === rep.id ? "#15803d" : "#475569",
                display: "flex",
                alignItems: "center",
                gap: "0.45rem",
                transition: "all 0.15s ease",
              }}
            >
              {rep.icon} {rep.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report Table Card */}
      <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        {selectedReport === "course-completion" && renderCourseCompletionReport()}
        {selectedReport === "quiz-results" && renderQuizResultsReport()}
        {selectedReport !== "course-completion" && selectedReport !== "quiz-results" && renderCourseCompletionReport()}
      </div>
    </div>
  );
}
